import find from 'lodash/find';
import isFunction from 'lodash/isFunction';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {window} from 'ssr-window';
import StorageUtils from '@spothero/utils/storage';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import withConfig from 'config/withConfig';
import SearchTracking from 'utils/search-tracking';
import {resetFilters} from 'filters/filters-actions';
import {push} from 'router/router-actions';
import {getSpotsRadial, getSpotsTiled} from 'spots/spots-actions';
import {
    toggleFiltersView,
    setSearchMapInstance,
    setZoom,
} from 'search/search-actions';
import {GOOGLE_MAPS_LAT_LNG} from '../search-prop-types';
import MonthlySupportMessage from './MonthlySupportMessage';
import SpotMap from './SpotMap';
import {notify} from 'notification/notification-actions';

const isBrowser = EnvironmentUtils.isBrowser();
const gmaps = isBrowser ? window.google.maps : null;

class SearchMap extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        searchRequestId: PropTypes.string,
        map: PropTypes.object,
        destination: PropTypes.shape({
            title: PropTypes.string.isRequired,
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired,
            position: GOOGLE_MAPS_LAT_LNG,
        }).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpot: PropTypes.object,
        zoom: PropTypes.number,
        starts: PropTypes.string,
        ends: PropTypes.string,
        isMonthly: PropTypes.bool,
        idealDistance: PropTypes.number,
        overrideIdealZoom: PropTypes.bool,
        searchString: PropTypes.string,
        hideRatesOnPins: PropTypes.bool,
        isFiltered: PropTypes.bool,
        isListViewActive: PropTypes.bool,
        monthlyPhoneNumber: PropTypes.string,
        monthlyPhoneNumberFriendly: PropTypes.string,
        mapLoadingIncrement: PropTypes.number,
        requestIncrements: PropTypes.number,
        loadRadialSpots: PropTypes.func,
        loadTiledSpots: PropTypes.func,
        toggleFiltersClick: PropTypes.func,
        resetFiltersClick: PropTypes.func,
        findParkingNearMe: PropTypes.func,
        setSearchMap: PropTypes.func,
        onZoom: PropTypes.func,
        kmlMapLayerEnabled: PropTypes.bool,
        kmlMapLayerURL: PropTypes.string,
        staticBaseUrl: PropTypes.string.isRequired,
        searchType: PropTypes.string.isRequired,
        geolocated: PropTypes.bool,
        pushTo: PropTypes.func.isRequired,
        sendNotification: PropTypes.func.isRequired,
        isMapboxEnabled: PropTypes.bool,
        isPolishTimepickersEnabled: PropTypes.bool,
    };
    static defaultProps = {
        isListViewActive: true,
    };
    state = {
        isMonthlySupportMessageDisabled:
            !this.props.monthlyPhoneNumber ||
            this.props.isPolishTimepickersEnabled
                ? true
                : StorageUtils.get('monthly-search-number', 'session'),
    };

    componentDidUpdate(prevProps, prevState) {
        const {isMobile, isListViewActive} = this.props;

        if (!isMobile && prevProps.isListViewActive !== isListViewActive) {
            this._transitionMap('list-view-hidden');
        }
    }

    _onCloseMonthlySupportMessageClick = () => {
        StorageUtils.set('monthly-search-number', true, 'session');

        this.setState({
            isMonthlySupportMessageDisabled: true,
        });
    };

    _onMarkerClick = spotId => {
        this.props.pushTo(`/spot-details/${spotId}`, {referrer: 'pin'});
    };

    _onMapMoved = ({zoomed = false, autoZoomed = false}) => {
        SearchTracking.updateAction(
            zoomed ? (autoZoomed ? 'AUTO_ZOOM' : 'ZOOM') : 'PAN'
        );
    };

    _transitionMap = toggleClassName => {
        const {spots, map} = this.props;
        const pageContainer = DOMUtils.el('.search-page');

        DOMUtils.toggleClass(pageContainer, toggleClassName);

        window.transitionEnd(pageContainer).bind(() => {
            if (this._spotMap && isFunction(this._spotMap.triggerResize)) {
                this._spotMap.triggerResize();
            }

            if (toggleClassName === 'list-view-hidden') {
                const activeSpot = find(spots, 'activeMarker');

                if (activeSpot && isBrowser) {
                    map.panTo(
                        new gmaps.LatLng(
                            activeSpot.latitude,
                            activeSpot.longitude
                        )
                    );
                }
            }

            window.transitionEnd(pageContainer).unbind();
        });
    };

    render() {
        const {
            isMobile,
            searchRequestId,
            spots,
            zoom,
            onZoom,
            isMonthly,
            isFiltered,
            destination,
            searchString,
            starts,
            ends,
            monthlyPhoneNumber,
            monthlyPhoneNumberFriendly,
            hideRatesOnPins,
            idealDistance,
            overrideIdealZoom,
            mapLoadingIncrement,
            requestIncrements,
            loadRadialSpots,
            loadTiledSpots,
            findParkingNearMe,
            toggleFiltersClick,
            resetFiltersClick,
            kmlMapLayerURL,
            kmlMapLayerEnabled,
            selectedSpot,
            setSearchMap,
            staticBaseUrl,
            searchType,
            geolocated,
            sendNotification,
            isMapboxEnabled,
            isPolishTimepickersEnabled,
        } = this.props;
        const {isMonthlySupportMessageDisabled} = this.state;

        return (
            <div className="SearchMap">
                <SpotMap
                    ref={node => {
                        this._spotMap = node;
                    }}
                    isMobile={isMobile}
                    searchRequestId={searchRequestId}
                    isFiltered={isFiltered}
                    zoom={zoom}
                    onZoom={onZoom}
                    idealDistance={idealDistance}
                    overrideIdealZoom={overrideIdealZoom}
                    destination={destination}
                    spots={spots}
                    isMonthly={isMonthly}
                    hideRatesOnPins={hideRatesOnPins}
                    searchString={searchString}
                    starts={starts}
                    ends={ends}
                    setSearchMap={setSearchMap}
                    mapLoadingIncrement={mapLoadingIncrement}
                    requestIncrements={requestIncrements}
                    onMarkerClick={this._onMarkerClick}
                    loadRadialSpots={loadRadialSpots}
                    loadTiledSpots={loadTiledSpots}
                    toggleFiltersClick={toggleFiltersClick}
                    resetFiltersClick={resetFiltersClick}
                    findParkingNearMe={findParkingNearMe}
                    kmlMapLayerURL={kmlMapLayerURL}
                    kmlMapLayerEnabled={kmlMapLayerEnabled}
                    selectedSpot={selectedSpot}
                    onMapMoved={this._onMapMoved}
                    staticBaseUrl={staticBaseUrl}
                    searchType={searchType}
                    geolocated={geolocated}
                    sendNotification={sendNotification}
                    isMapboxEnabled={isMapboxEnabled}
                    isPolishTimepickersEnabled={isPolishTimepickersEnabled}
                />
                {isMonthly && !isMobile && !isMonthlySupportMessageDisabled && (
                    <MonthlySupportMessage
                        monthlyPhoneNumber={monthlyPhoneNumber}
                        monthlyPhoneNumberFriendly={monthlyPhoneNumberFriendly}
                        onCloseMonthlySupportMessage={
                            this._onCloseMonthlySupportMessageClick
                        }
                    />
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        search: {
            data: {
                currentIncrement,
                requestIncrements,
                zoom,
                overrideIdealZoom,
                destinationTitle,
                selectedSpot,
                isListVisible,
                kmlMapLayerEnabled,
                kmlMapLayerURL,
                searchMapInstance,
                searchType,
                geolocated,
            },
        },
        searchRequest: {
            id,
            starts,
            ends,
            monthly,
            latitude,
            longitude,
            search_string: searchString,
            ideal_search_distance: idealDistance,
        },
        filters: {numActive},
        user: {
            data: {experiments},
        },
    } = state;

    return {
        searchRequestId: id,
        starts,
        ends,
        isMonthly: monthly,
        selectedSpot,
        isListViewActive: isListVisible,
        isFiltered: numActive > 0,
        searchString,
        destination: {
            title: destinationTitle,
            latitude,
            longitude,
            position: isBrowser ? new gmaps.LatLng(latitude, longitude) : null,
        },
        map: searchMapInstance,
        zoom,
        idealDistance,
        overrideIdealZoom,
        mapLoadingIncrement: currentIncrement,
        requestIncrements,
        kmlMapLayerEnabled,
        kmlMapLayerURL,
        searchType,
        geolocated,
        isMapboxEnabled: experiments.mapbox_test === 'mapbox',
    };
};

const mapDispatchToProps = {
    loadRadialSpots: getSpotsRadial,
    loadTiledSpots: getSpotsTiled,
    resetFiltersClick: resetFilters,
    toggleFiltersClick: toggleFiltersView,
    setSearchMap: setSearchMapInstance,
    onZoom: setZoom,
    pushTo: push,
    sendNotification: notify,
};

const enhance = compose(
    withConfig(['isMobile', 'staticBaseUrl']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(SearchMap);
